import { Mandates, Principal, PrincipalInformationAndFeatures } from '../types';
import { principalServiceRequest } from './base-request';
import { AssignmentsDto } from './dto/Assignments.dto';
import { GetFeaturesDto } from './dto/GetFeatures.dto';
import {
  ExpansionOpportunityResponse,
  PostEdiFormRequestDto,
  PrincipalAssignmentFilter,
  PrincipalLogoUploadResponse,
} from './principal-endpoints.types';

export const postPrincipalEdi = async (
  principalId: string,
  contactForm: PostEdiFormRequestDto,
) => {
  return principalServiceRequest<void>(
    'post',
    `principals/${principalId}/edi`,
    contactForm,
  );
};

export const getPrincipalById = async (principalId: string) => {
  return principalServiceRequest<Principal>('get', `principals/${principalId}`);
};

export const getPrincipalFeatures = async (principalId: string) => {
  return principalServiceRequest<GetFeaturesDto>(
    'get',
    `principals/${principalId}/features`,
  );
};

export const createPrincipal = async ({
  id,
  ...requestBody
}: PrincipalInformationAndFeatures): Promise<void> => {
  await principalServiceRequest('put', `principals/${id}`, requestBody);
};

export const updatePrincipalInformation = async ({
  id,
  disabledFeatures,
  maintenanceMode,
  customerData,
  name,
  manufacturerName,
  adGroupId,
  portfolioLevel,
  slug,
  teamId,
  logoUrl,
}: PrincipalInformationAndFeatures): Promise<void> => {
  await principalServiceRequest('patch', `principals/${id}`, {
    disabledFeatures,
    maintenanceMode,
    customerData,
    name,
    manufacturerName,
    adGroupId,
    portfolioLevel,
    slug,
    teamId,
    logoUrl,
  });
};

const buildQueryString = (filter: PrincipalAssignmentFilter | undefined) => {
  if (!filter || filter.hasAllMandates) return '';

  return (
    '?' +
    Object.entries(filter)
      .map(([key, value]) => {
        if ((Array.isArray(value) && !value.length) || !value) return;
        return `${key}=${value.toString()}`;
      })
      .filter((stringParam): stringParam is string => !!stringParam)
      .join('&')
  );
};

export const getMyAssignments = async (principalId: string) => {
  const response = await principalServiceRequest<AssignmentsDto>(
    'get',
    `principals/${principalId}/assignments/me`,
  );
  return response.data;
};

export const getPrincipalMandateAssignments = async (
  principalId: string,
  filter?: PrincipalAssignmentFilter,
) => {
  const queryString = buildQueryString(filter);
  const response = await principalServiceRequest<Mandates>(
    'get',
    `principals/${principalId}/assignments${queryString}`,
  );
  return response.data;
};

export const getNewPrincipalMandateAssignments = async (
  principalId: string,
  portfolioLevel: number,
) => {
  const response = await principalServiceRequest<Mandates>(
    'get',
    `principals/${principalId}/assignments?portfolioLevel=${portfolioLevel}`,
  );
  return response.data;
};

export const getExpansionOpportunities = (principalId: string) => {
  return principalServiceRequest<ExpansionOpportunityResponse>(
    'Get',
    `principals/${principalId}/expansion-opportunities`,
  );
};

export const uploadNewPrincipalLogo = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  return principalServiceRequest<PrincipalLogoUploadResponse>(
    'post',
    'principals/logos',
    formData,
  );
};
